import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { AuthProvider } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import config from 'config';
import { User, WebStorageStateStore } from 'oidc-client-ts';
import { sanitizeReturnTo } from 'utils/returnTo';

export interface MultipleAuthProviderInterface {
    setProvider: (provider: AuthProviderType) => void;
    isProviderChanged: boolean;
}

export interface MultipleAuthProvidersOptions {
    children?: React.ReactNode;
}

export enum AuthProviderType {
    Zitadel,
    Okta,
}

export const useMultipleProviders = () => useContext(MultipleAuthContext);

export const MultipleAuthContext = createContext<MultipleAuthProviderInterface>({} as MultipleAuthProviderInterface);

export const MultipleAuthProviders = (opts: MultipleAuthProvidersOptions) => {
    let storeProvider = localStorage.getItem('provider');
    let parsedValue =
        storeProvider == null || storeProvider == ''
            ? null
            : AuthProviderType[storeProvider as keyof typeof AuthProviderType];

    const [currentProvider, setCurrentProvider] = useState(parsedValue);
    const [isProviderChanged, setIsProviderChanged] = useState(false);
    const navigate = useNavigate();

    const [providers] = useState([
        {
            provider: AuthProviderType.Zitadel,
            config: {
                authority: `${config.ZITADEL_AUTHORITY || ''}`,
                client_id: `${config.ZITADEL_CLIENT_ID || ''}`,
                scope: 'openid profile email offline_access',
                redirect_uri: `${window.location.origin}/home`,
                post_logout_redirect_uri: `${window.location.origin}/account/login`,
            },
        },
    ]);

    const setProvider = useCallback(
        (provider: AuthProviderType | null) => {
            localStorage.setItem('provider', provider?.toString() ?? '');
            setIsProviderChanged(true);
            setCurrentProvider(provider);
        },
        [setCurrentProvider]
    );

    // Zitadel always redirects back to redirect_uri, so send the user on to the page they originally
    // requested (passed as OIDC state by the login page). Otherwise just strip the auth params from the url.
    const onSigninCallback = useCallback(
        (user: User | undefined) => {
            const returnTo = sanitizeReturnTo((user?.state as { returnTo?: unknown } | undefined)?.returnTo);
            navigate(returnTo ?? window.location.pathname, { replace: true });
        },
        [navigate]
    );

    const contextValue = useMemo<MultipleAuthProviderInterface>(() => {
        return {
            setProvider,
            isProviderChanged,
        };
    }, [setProvider, isProviderChanged]);

    const selectedProvider = providers.find(
        (x) =>
            currentProvider == null ||
            x.provider === currentProvider ||
            AuthProviderType[x.provider].toString() == currentProvider.toString()
    );

    return (
        <MultipleAuthContext.Provider value={contextValue}>
            <AuthProvider
                onSigninCallback={onSigninCallback}
                stateStore={new WebStorageStateStore({ store: window.localStorage })}
                userStore={new WebStorageStateStore({ store: window.localStorage })}
                key={currentProvider}
                {...selectedProvider?.config}>
                {opts.children}
            </AuthProvider>
        </MultipleAuthContext.Provider>
    );
};

export default MultipleAuthProviders;
